import {
  bffCrmCareTeamSwapJobs,
  bffCrmCareTeamSwapLookups,
  bffCrmCareTeamSwapSearch,
} from '@fireflyhealth/api'
import type {
  BffCrmCareTeamSwapJobsCreateBodyParams,
  BffCrmCareTeamSwapJobsRetrieveResponse,
  BffCrmCareTeamSwapLookupsRetrieveResponse,
  BffCrmCareTeamSwapSearchCreateBodyParams,
  BffCrmCareTeamSwapSearchCreateQueryParams,
  BffCrmCareTeamSwapSearchCreateResponse,
} from '@fireflyhealth/api'
import { useMutation, useQuery } from '~/shared/hooks/query'
import { required } from '~/shared/lib/required'
export type CareTeamSwapLookups = BffCrmCareTeamSwapLookupsRetrieveResponse
export type CareTeamSwapSearchBody = BffCrmCareTeamSwapSearchCreateBodyParams
export type CareTeamSwapSearchResponse = BffCrmCareTeamSwapSearchCreateResponse
// limit/offset window into the (server-sorted) cohort.
type CareTeamSwapSearchPage = BffCrmCareTeamSwapSearchCreateQueryParams
type CareTeamSwapJobBody = BffCrmCareTeamSwapJobsCreateBodyParams
export type CareTeamSwapJob = BffCrmCareTeamSwapJobsRetrieveResponse
export type CareTeamSwapJobStatus = CareTeamSwapJob['status']

// Terminal statuses stop the poll; pending / in-progress keep it running.
const TERMINAL_JOB_STATUSES: CareTeamSwapJobStatus[] = ['complete', 'skipped', 'failed']
export const isTerminalJobStatus = (status: CareTeamSwapJobStatus | undefined) =>
  status != null && TERMINAL_JOB_STATUSES.includes(status)

const careTeamSwapLookupsQueryKey = () => bffCrmCareTeamSwapLookups.retrieveQueryKey()

/** Dropdown options (providers, states, contracts, enrollment statuses, clinical roles). */
export const useCareTeamSwapLookups = () =>
  useQuery<CareTeamSwapLookups>(
    careTeamSwapLookupsQueryKey(),
    () => bffCrmCareTeamSwapLookups.retrieve().then(r => r.data),
    { staleTime: Infinity },
    { error: 'Failed to load care team reassignment options' },
  )

const careTeamSwapSearchQueryKey = (body: CareTeamSwapSearchBody, page: CareTeamSwapSearchPage) =>
  ['careTeamSwapSearch', body, page] as const

/**
 * The cohort paneled with the selected current providers, paginated at the backend (ordered by id).
 * Each row carries its current-team licensure; when `body.swaps` is set the rows also carry the
 * resulting (post-swap) care team and its licensure, computed for the returned page only. `page` is
 * the limit/offset window. Pass `null` for `body` to leave the query disabled (e.g. before a search
 * is submitted).
 */
export const useCareTeamSwapSearch = (
  body: CareTeamSwapSearchBody | null,
  page: CareTeamSwapSearchPage,
) =>
  useQuery<CareTeamSwapSearchResponse>(
    body
      ? careTeamSwapSearchQueryKey(body, page)
      : careTeamSwapSearchQueryKey({ current_provider_ids: [] }, page),
    () =>
      bffCrmCareTeamSwapSearch
        .create({ data: required(body, 'a search body'), params: page })
        .then(r => r.data),
    { enabled: !!body },
    { error: 'Care team search failed' },
  )

/** Create the reassignment job (writes the job + its swap rows); returns id + pending status. */
export const useCreateCareTeamSwapJob = () =>
  useMutation<CareTeamSwapJob, unknown, CareTeamSwapJobBody>(
    body => bffCrmCareTeamSwapJobs.create({ data: body }).then(r => r.data),
    undefined,
    { error: 'Failed to start the reassignment' },
  )

/**
 * Poll a reassignment job to its terminal status. Disabled until a job id is set; polls every
 * two seconds while pending / in-progress and stops once the job reaches a terminal status.
 */
export const useCareTeamSwapJobPoll = (jobId: number | null) =>
  useQuery<CareTeamSwapJob>(
    jobId != null
      ? bffCrmCareTeamSwapJobs.retrieveQueryKey({ id: jobId })
      : ['retrieveBffCrmCareTeamSwapJobs', null],
    () => bffCrmCareTeamSwapJobs.retrieve({ id: required(jobId, 'a job id') }).then(r => r.data),
    {
      enabled: jobId != null,
      refetchInterval: query => (isTerminalJobStatus(query.state.data?.status) ? false : 2000),
    },
  )
